import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ShareCard from "@/components/ShareCard";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Copy, MessageCircle, Loader2 } from "lucide-react";

export default function ShareModal({ hostel, open, onClose }) {
  const cardRef = useRef();
  const [busy, setBusy] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/hostels/${hostel?.id || ""}`;
  const message = hostel
    ? `🛏️ ${hostel.name} — beds from ₹${hostel.price_per_bed}/night in ${hostel.city}${
        hostel.coaching_centers?.[0] ? `, walking distance to ${hostel.coaching_centers[0]}` : ""
      }. Book on HostelNow → ${shareUrl}`
    : "";

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        skipFonts: false,
      });
      const link = document.createElement("a");
      link.download = `hostelnow-${(hostel.name || "listing").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded — ready to share!");
    } catch (e) {
      toast.error("Couldn't generate the card. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Caption copied to clipboard");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const openWhatsApp = () => {
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encoded}`, "_blank", "noopener,noreferrer");
  };

  if (!hostel) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share this listing</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-[1fr_320px] gap-6">
          {/* Card preview (scaled down) */}
          <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 p-4 flex items-center justify-center">
            <div
              className="origin-top"
              style={{ transform: "scale(0.35)", width: "1080px", height: "1350px" }}
            >
              <ShareCard ref={cardRef} hostel={hostel} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-500 mb-2">
                Caption (WhatsApp / Instagram)
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{message}</p>
            </div>

            <Button
              data-testid="share-download"
              onClick={download}
              disabled={busy}
              className="w-full rounded-full bg-orange-800 hover:bg-orange-800 text-white h-12 gap-2"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
              {busy ? "Generating…" : "Download 1080×1350 PNG"}
            </Button>

            <Button
              data-testid="share-copy-caption"
              onClick={copyMessage}
              variant="outline"
              className="w-full rounded-full h-12 gap-2"
            >
              <Copy size={16} /> Copy caption
            </Button>

            <Button
              data-testid="share-whatsapp"
              onClick={openWhatsApp}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 gap-2"
            >
              <MessageCircle size={16} /> Share on WhatsApp
            </Button>

            <p className="text-xs text-slate-500 leading-relaxed">
              Downloaded image is optimized for Instagram Stories, WhatsApp status, and Facebook feed.
              For best results, download the PNG and attach it separately along with the caption.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
